import React from 'react';
import { Building2, Briefcase, Globe, FileText, Scale, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { practiceAreas } from '../utils/mock';
import PageTitle from '../hooks/PageTitle';

const PracticeAreas = () => {
  const iconMap = {
    Building2,
    Briefcase,
    Globe,
    FileText,
    Scale,
    Shield
  };

  return (
    <div className="min-h-screen pt-20">
      <PageTitle title="Practice Areas" />
      {/* Hero Section */}
      <section className="relative py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-serif mb-6">
              Practice Areas
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Comprehensive legal expertise across corporate, commercial, and international matters
            </p>
          </div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {practiceAreas.map((area) => {
              const IconComponent = iconMap[area.icon] || Building2;

              return (
                <Card key={area.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 group">
                  <CardContent className="p-10">
                    <div className="flex items-start space-x-6">
                      <div className="w-16 h-16 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-serif text-gray-900 mb-4">{area.title}</h3>
                        <p className="text-gray-600 leading-relaxed mb-4">{area.description}</p>
                        <p className="text-gray-500 text-sm leading-relaxed border-l-4 border-gray-300 pl-4">
                          {area.details}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sector Experience */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-gray-900 mb-4">Sector Experience</h2>
            <p className="text-lg text-gray-600">Deep industry knowledge across key sectors</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              'Financial Services',
              'Technology',
              'Private Equity',
              'Real Estate',
              'Energy & Natural Resources',
              'Healthcare & Life Sciences',
              'Manufacturing',
              'Telecommunications'
            ].map((sector, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <p className="text-gray-800 font-medium">{sector}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-serif mb-6">Discuss Your Legal Requirements</h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Our team is ready to provide expert counsel tailored to your specific needs
          </p>
          <a
            href="/contact"
            className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-gray-900 bg-white rounded-md hover:bg-gray-100 transition-colors"
          >
            Contact Us Today
          </a>
        </div>
      </section>
    </div>
  );
};

export default PracticeAreas;
